'use client';
import { useTranslations } from 'next-intl';
import cx from 'classnames';
import {
  capitalizeText,
  getProductName
} from '@cat-ecom/common/utils/stringUtils';
import { navigateTo } from '@cat-ecom/common/utils/browserUtils';
import { PAGE_BREAKPOINTS } from '@cat-ecom/pcc-components/src/constants/commonConstants';
import { Conditional } from '@cat-ecom/pcc-components';
import { BlocksCloseEvent } from '@blocks-web-components/events';
import { TextHighlight, useBreakpoint } from 'components/pcc-components';
import { Image } from 'components/image';
import {
  CatTooltip,
  CatCard,
  CatHeading,
  CatIconSuccessFilled,
  CatTextPassage,
  CatButton
} from 'components/blocks-components';
import { getFamilyName } from 'services/equipment/utils';
import { redirectFromEquipment } from 'app/[lang]/[esite]/(core)/my-equipment/utils/utils';
import { MyEquipmentCardProps } from '../types';
import { MyEquipmentActionMenu } from '../my-equipment-action-menu';
import { useMyEquipmentContext } from '../my-equipment-search-pagination-context';
import { ServiceInsightIcon } from '../service-insight-icon/ServiceInsightIcon';
import { MY_EQP_SCHEMA } from '../constants';
import style from './my-equipment-multiple-card.module.scss';

export const MyEquipmentMultipleCard = ({
  equipment,
  searchTerm,
  selectTooltipText,
  uniqueIndex,
  userDetail,
  isRequestServiceFlag,
  startTransition,
  serviceInsightsDetails,
  isAssetLandingFeatureFlag
}: Readonly<MyEquipmentCardProps>) => {
  const { seoUrl = '', model, name, productFamily } = equipment;
  const isDesktop = useBreakpoint(PAGE_BREAKPOINTS.XL);
  const t = useTranslations();
  const nickName = capitalizeText(equipment?.assetName || '');
  const { selectedEquipmentIndex } = useMyEquipmentContext();

  const familyName = getFamilyName(model, name ?? productFamily);
  const equipmentName = `${equipment?.model} ${getProductName(familyName)}`;

  const isSelectedEquipment = uniqueIndex === equipment?.uniqueIndex;

  const handleClose = (e: BlocksCloseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
  };
  const redirectToViewDashboard = () => {
    seoUrl && navigateTo(seoUrl);
  };

  const handleRequestService = async () => {
    const queryParam = new URLSearchParams({
      isRequestServiceModal: 'true'
    });
    redirectFromEquipment(
      `${seoUrl}?${queryParam.toString()}`,
      isSelectedEquipment,
      equipment
    );
  };

  const kebabActions = [
    {
      id: MY_EQP_SCHEMA.VIEW_DASHBOARD,
      isSelectedEquipment
    },
    {
      id: MY_EQP_SCHEMA.SHOP_PARTS,
      isSelectedEquipment
    },
    {
      id: MY_EQP_SCHEMA.CAT_PARTS_DIAGRAM,
      isSelectedEquipment
    },
    {
      id: MY_EQP_SCHEMA.REQUEST_SERVICE,
      ...(!isRequestServiceFlag && {
        onClick: () => {
          handleRequestService();
        }
      })
    },
    {
      id: MY_EQP_SCHEMA.ALP_MAINTENANCE_REPAIRS,
      isSelectedEquipment
    },
    {
      id: MY_EQP_SCHEMA.CLEAR_SELECTION,
      isActionVisible: isSelectedEquipment
    },
    {
      id: MY_EQP_SCHEMA.PLEASE_SELECT,
      isActionVisible: !isSelectedEquipment
    },
    {
      id: MY_EQP_SCHEMA.EDIT
    },
    {
      id: MY_EQP_SCHEMA.DELETE
    }
  ];

  return (
    <div
      className={`${style['equipment-card']} ${isSelectedEquipment && style['selected-equipment-card']}`}
    >
      <CatCard
        removePadding
        data-testid="desktop-card"
        variant={isSelectedEquipment ? 'bare' : null}
      >
        <div className={style['equipment-card__container']}>
          <div className={style['equipment-card__icon']} slot="header">
            <ServiceInsightIcon
              equipmentDetails={equipment}
              highlightSelectedEquipment={isSelectedEquipment}
              isMultiple
              serviceInsightsDetails={serviceInsightsDetails}
              isCspUser={userDetail?.isCspUser}
            />
            {isSelectedEquipment && (
              <CatTooltip onBlTooltipClose={handleClose} position="auto">
                <CatIconSuccessFilled
                  slot="trigger"
                  data-testid="success-icon"
                  tabIndex={0}
                  aria-label={selectTooltipText}
                  className={style['equipment-card__success']}
                />
                <CatTextPassage size="xs">{selectTooltipText}</CatTextPassage>
              </CatTooltip>
            )}
          </div>
          <Image
            src={equipment?.imageURL}
            slot="header"
            alt={equipmentName}
            className={style['equipment-card__image']}
            width={100}
            height={65}
            loading="lazy"
          />
          <div slot="footer" className="w-100">
            <div
              className={`d-flex justify-content-between ${!isDesktop && 'align-items-center'}`}
            >
              <div className={style['equipment-card__title-wrapper']}>
                {nickName && (
                  <CatHeading variant="display-sm">
                    <span
                      className={cx(
                        'text-truncate d-block',
                        style['equipment-card__title-width']
                      )}
                    >
                      <TextHighlight
                        text={equipment?.assetName}
                        textToHighlight={searchTerm}
                        isSpecialCharacters
                      />
                    </span>
                  </CatHeading>
                )}
                {equipmentName && (
                  <CatHeading variant={nickName ? 'footnote' : 'display-sm'}>
                    <span
                      className={cx(
                        'text-truncate d-block',
                        style['equipment-card__title-width']
                      )}
                    >
                      <TextHighlight
                        text={equipmentName}
                        textToHighlight={searchTerm}
                        isSpecialCharacters
                      />
                    </span>
                  </CatHeading>
                )}
                {equipment?.serialNumber && (
                  <CatHeading variant="footnote">
                    <TextHighlight
                      text={equipment?.serialNumber}
                      textToHighlight={searchTerm}
                      isSpecialCharacters
                      highlightClassName={style['equipment-card__title-width']}
                    />
                  </CatHeading>
                )}
                {!isDesktop &&
                  isDesktop != undefined &&
                  isSelectedEquipment && (
                    <CatButton
                      className={style['equipment-card__view-dashboard-btn']}
                      variant="ghost"
                      data-testid="view-dashboard-btn"
                      onClick={redirectToViewDashboard}
                    >
                      <span
                        className={cx(
                          'text-truncate d-block',
                          style['equipment-card__view-dashboard-text-width']
                        )}
                      >
                        {t('VIEW_DASHBOARD')}
                      </span>
                    </CatButton>
                  )}
              </div>
              <Conditional test={Object.keys(equipment).length > 0}>
                <MyEquipmentActionMenu
                  actionItems={kebabActions}
                  equipmentDetails={equipment}
                  userDetail={userDetail}
                  startTransition={startTransition}
                  selectedEquipmentIndex={selectedEquipmentIndex}
                  equipmentName={equipmentName}
                  isAssetLandingFeatureFlag={isAssetLandingFeatureFlag}
                />
              </Conditional>
            </div>
          </div>
        </div>
      </CatCard>
    </div>
  );
};
